import useFullyFledgedField from '@gef-ui/form-core/hooks/field/useFullyFledgedField';
import chainHandlers from "@gef-ui/utils/chainHandlers";
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DesktopDatePicker as MuiDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { validationDisplayTypes } from '../constants';
import { createBaseInputProps, createPickerButtonSlot, resolveHiddenProp, resolveInputLabelProps, resolveValidationColor, resolveVariant, useFieldDefaultValue, useHelperText } from '../utils';
const CreatePicker = (inProps)=>{
    const props = useFieldDefaultValue({
        inProps,
        name: inProps.componentName
    });
    const { defaultDateFormat, PickerComponent, fieldName, fieldIndices, initiallyDirty, modelParser, viewParser, onBlur, disabled: disabledByProp, defaultValue, clearable, label, componentName, validationDisplay = validationDisplayTypes.PLAIN_TEXT, onChange, TextFieldProps = {}, helperText, tooltip, variant, ...other } = props;
    const { errorMessage, handleChange, handleBlur, localizedLabel, fieldId, value, disabled, validationType, asyncValidationInProgress, hidden } = useFullyFledgedField({
        fieldName,
        fieldIndices,
        initiallyDirty,
        modelParser,
        viewParser,
        disabled: disabledByProp,
        defaultValue
    });
    const validationColor = resolveValidationColor(validationType, validationDisplay);
    const errorOrHelperText = useHelperText(validationDisplay, errorMessage, helperText);
    const { input = {}, inputLabel = {}, formHelperText = {}, htmlInput = {} } = TextFieldProps.slotProps ?? {};
    const { endAdornment, ...InputProps } = useMemo(()=>createBaseInputProps({
            tooltipValidation: validationDisplay === validationDisplayTypes.TOOLTIP,
            validationColor,
            asyncValidationInProgress,
            errorMessage,
            tooltip,
            variant
        }), [
        validationColor,
        validationDisplay,
        errorMessage,
        tooltip,
        variant,
        asyncValidationInProgress
    ]);
    const PickerButton = useMemo(()=>createPickerButtonSlot(endAdornment), [
        endAdornment
    ]);
    return /*#__PURE__*/ React.createElement(PickerComponent, {
        ...disabled ? {
            disabled
        } : {},
        format: defaultDateFormat,
        value: value ?? null,
        onClose: chainHandlers([
            handleBlur,
            onBlur
        ]),
        onChange: chainHandlers([
            (val)=>handleChange(undefined, val, false, true),
            onChange
        ]),
        label: label ?? localizedLabel,
        slots: {
            openPickerButton: PickerButton
        },
        slotProps: {
            field: {
                clearable
            },
            textField: {
                label: label ?? localizedLabel,
                helperText: errorOrHelperText,
                color: validationColor,
                onBlur: chainHandlers([
                    handleBlur,
                    onBlur
                ]),
                InputProps: {
                    ...InputProps,
                    ...input
                },
                InputLabelProps: {
                    ...resolveInputLabelProps(variant, validationColor, other),
                    ...inputLabel
                },
                FormHelperTextProps: {
                    validation: validationColor,
                    ...formHelperText
                },
                inputProps: {
                    inputMode: 'numeric',
                    ...htmlInput
                },
                id: fieldId,
                ...resolveVariant(variant),
                ...TextFieldProps,
                ...resolveHiddenProp(hidden, TextFieldProps)
            }
        },
        ...other
    });
};
const propTypes = {
    PickerComponent: PropTypes.any,
    TextFieldProps: PropTypes.object,
    clearable: PropTypes.bool,
    componentName: PropTypes.string,
    defaultDateFormat: PropTypes.string,
    defaultValue: PropTypes.instanceOf(Date),
    disabled: PropTypes.bool,
    fieldIndices: PropTypes.arrayOf(PropTypes.number),
    fieldName: PropTypes.string,
    helperText: PropTypes.node,
    initiallyDirty: PropTypes.bool,
    modelParser: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    tooltip: PropTypes.string,
    validationDisplay: PropTypes.oneOf(Object.keys(validationDisplayTypes)),
    variant: PropTypes.string,
    viewParser: PropTypes.func
};
CreatePicker.propTypes = propTypes;
export const DATE_PICKER_COMPONENT_NAME = 'DatePicker';
const defaultDatePickerFormat = 'dd/MM/yyyy';
export const DatePicker = (props)=>/*#__PURE__*/ React.createElement(CreatePicker, {
        PickerComponent: MuiDatePicker,
        componentName: DATE_PICKER_COMPONENT_NAME,
        defaultDateFormat: defaultDatePickerFormat,
        ...props
    });
DatePicker.propTypes = propTypes;
export const DATE_TIME_PICKER_COMPONENT_NAME = 'DateTimePicker';
const defaultDateTimePickerFormat = 'dd/MM/yyyy HH:mm';
export const DateTimePicker = (props)=>/*#__PURE__*/ React.createElement(CreatePicker, {
        PickerComponent: MuiDateTimePicker,
        componentName: DATE_TIME_PICKER_COMPONENT_NAME,
        defaultDateFormat: defaultDateTimePickerFormat,
        ampm: false,
        ...props
    });
DateTimePicker.propTypes = propTypes;
export const TIME_PICKER_COMPONENT_NAME = 'TimePicker';
const defaultTimePickerFormat = 'HH:mm';
export const TimePicker = (props)=>/*#__PURE__*/ React.createElement(CreatePicker, {
        PickerComponent: MuiTimePicker,
        componentName: TIME_PICKER_COMPONENT_NAME,
        defaultDateFormat: defaultTimePickerFormat,
        ampm: false,
        ...props
    });
TimePicker.propTypes = propTypes;
